import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

// Maps-Key kommt zur Laufzeit aus functions.php (data-maps-key), nie aus dem Build
function getMapsKey() {
  if (typeof document === "undefined" || !document.documentElement || !document.documentElement.dataset) {
    return "";
  }

  const key = document.documentElement.dataset.mapsKey;
  return typeof key === "string" ? key.trim() : "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLatLng(lat, lng) {
  return isFiniteNumber(lat) && isFiniteNumber(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180;
}

function str(v) {
  if (typeof v === "string") {
    return v.trim();
  }

  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }

  return "";
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    // ohne Orts- und Straßenangabe bleibt der Name als Beschriftung erhalten
    if (!g.city && !g.street && g.name) {
      p.unshift(g.name);
    }

    if (g.state && !g.street && p.indexOf(g.state) === -1) {
      p.push(g.state);
    }

    if (g.country && !g.street && p.indexOf(g.country) === -1) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Feature der Geocoder-API in das bisherige Adressformat übersetzen; das Feature bleibt unverändert
  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!validLatLng(lat, lng)) {
      return null;
    }

    const props = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = str(props.type);
    const name = str(props.name);
    const geoaddress = {};

    let street = str(props.street);
    let city = str(props.city);
    let district = str(props.district);

    if (type) {
      if (type === "street" && !street) {
        street = name;
      } else if (type === "city") {
        city = name || city;
      } else if (type === "district" || type === "locality") {
        if (name && name !== city) {
          district = name;
        }
      }
    } else {
      // Legacy-Fallback für Antworten ohne type
      const osmKey = str(props.osm_key);
      const osmValue = str(props.osm_value);

      if (osmKey === "highway" && !street) {
        street = name;
      }

      if (!city && name && osmKey === "place" && ["city", "town", "village"].indexOf(osmValue) !== -1) {
        city = name;
      }

      if (city && name && name !== city &&
        (osmKey === "boundary" || (osmKey === "place" && osmValue === "suburb"))) {
        district = name;
      }
    }

    if (district && district === city) {
      district = "";
    }

    const housenumber = str(props.housenumber);
    const postcode = str(props.postcode);

    if (street) {
      geoaddress["street"] = street;
      if (housenumber) {
        geoaddress["number"] = housenumber;
      }
    }

    if (postcode) {
      geoaddress["zip"] = postcode;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district) {
      geoaddress["district"] = district;
    }

    if (name && !city && !street) {
      geoaddress["name"] = name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(props.state) || undefined;
    geoaddress["country"] = str(props.country) || undefined;

    geoaddress["text"] = this._geoaddressText(geoaddress);
    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(data) {
    const results = [];

    if (data && Array.isArray(data.features)) {
      data.features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });
    }

    return results;
  }

  // löst immer auf: Treffer, bei Abbruch oder ohne Key eine leere Liste; Fehler und Timeout lehnen ab
  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const key = getMapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      return this._features(res.data);
    }).catch(thrown => {
      if (this.axios.isCancel(thrown)) {
        return [];
      }

      throw thrown;
    }).then(results => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }

      return results;
    }, err => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }

      throw err;
    });
  }

  // löst mit der Adresse auf, ohne Key, bei ungültigen Koordinaten oder ohne Treffer mit null
  reverse(lat, lng) {
    const key = getMapsKey();
    const nLat = Number(lat);
    const nLng = Number(lng);

    if (!key || !validLatLng(nLat, nLng)) {
      return Promise.resolve(null);
    }

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: nLat,
        lon: nLng,
        lang: "de",
        key
      }
    }).then(res => {
      const results = this._features(res.data);
      return results.length > 0 ? results[0] : null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory, Geocoder };

export default {};
